/**
 * @fileoverview Auth gate for the web runtime.
 *
 * - When auth is `disabled` (no Supabase / desktop / local web), renders the
 *   app directly with no login requirement.
 * - When `signedOut`, renders the sign-in screen.
 * - When `signedIn`, renders the app.
 */

import { useEffect, useState, type ReactNode } from 'react'
import { useAuth } from './AuthContext'
import { useI18n } from '../i18n/context'
import { OPEN_ALBUM_SELECTION_EVENT, OPEN_HOME_EVENT } from '../app/events'
import { AuthScreen } from '../website/AuthScreen'

export function AuthGate({ children }: { children: ReactNode }): JSX.Element {
  const { status, user, signOut, shouldResetPassword } = useAuth()
  const { t } = useI18n()
  const [showAppContent, setShowAppContent] = useState(false)
  const [showPublicHome, setShowPublicHome] = useState(false)
  const [pendingAlbumSelection, setPendingAlbumSelection] = useState(false)

  useEffect(() => {
    function handleOpenHome(): void {
      setShowPublicHome(true)
      setShowAppContent(false)
    }

    window.addEventListener(OPEN_HOME_EVENT, handleOpenHome)
    return () => window.removeEventListener(OPEN_HOME_EVENT, handleOpenHome)
  }, [])

  useEffect(() => {
    if (status !== 'signedIn') {
      setShowPublicHome(false)
      setShowAppContent(false)
    }
  }, [status])

  useEffect(() => {
    if (!pendingAlbumSelection) return
    const isAppMounted =
      (status === 'disabled' && !showPublicHome) ||
      (status === 'signedIn' && showAppContent && !showPublicHome)
    if (!isAppMounted) return

    window.dispatchEvent(new Event(OPEN_ALBUM_SELECTION_EVENT))
    setPendingAlbumSelection(false)
  }, [pendingAlbumSelection, showAppContent, showPublicHome, status])

  function openAlbumSelection(): void {
    setPendingAlbumSelection(true)
    setShowPublicHome(false)
    setShowAppContent(true)
  }

  if (status === 'disabled') {
    if (showPublicHome) {
      return <AuthScreen entryMode="checkAlbums" onCheckAlbums={openAlbumSelection} />
    }

    return <>{children}</>
  }
  if (status === 'signedIn' && !shouldResetPassword) {
    if (showPublicHome) {
      return <AuthScreen entryMode="checkAlbums" onCheckAlbums={openAlbumSelection} />
    }

    if (showAppContent) {
      return (
        <>
          <div className="auth-account-bar">
            <span className="auth-account-email">{user?.email}</span>
            <button type="button" className="auth-link" onClick={() => void signOut()}>
              {t.authSignOut}
            </button>
          </div>
          {children}
        </>
      )
    }

    return (
      <div className="auth-screen">
        <div className="auth-layout">
          <div className="auth-cta-panel" aria-label={t.authSubtitle}>
            <h2 className="auth-title">{t.appName}</h2>
            <p className="auth-subtitle">{t.authSubtitle}</p>
            <div className="auth-cta-actions">
              <button type="button" className="auth-submit" onClick={openAlbumSelection}>
                {t.checkYourAlbums}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }
  if (status === 'loading') {
    return (
      <div className="auth-screen">
        <p className="auth-subtitle">{t.loading}</p>
      </div>
    )
  }

  if (showPublicHome) {
    return <AuthScreen />
  }

  return <AuthScreen />
}
